import { Box, Button, Portal } from '@mui/material';
import {
  DeleteButton,
  useNotify,
  useRecordContext,
  useRedirect,
  useResourceContext,
  useSaveContext
} from 'react-admin';
import { useFormContext, useFormState } from 'react-hook-form';
import AppBarActionsPortal from './AppBarActionsPortal.tsx';
import { useKeyboardInset } from './useKeyboardInset.ts';
import { saveBarZIndex } from '../theme.ts';
import { useNavigate, useSearchParams } from 'react-router-dom';

type AppBarSaveActionsProps = {
  redirectTo?: 'list' | 'show' | 'edit' | false;
  resourceOverride?: string;
  showDelete?: boolean;
};

type EditableRecord = Record<string, unknown> & {
  id?: string | number;
};

const AppBarSaveActions = ({
  redirectTo = 'list',
  resourceOverride,
  showDelete = true
}: AppBarSaveActionsProps) => {
  const { save } = useSaveContext();
  const notify = useNotify();
  const redirect = useRedirect();
  const record = useRecordContext<EditableRecord>();
  const resourceFromContext = useResourceContext();
  const resource = resourceOverride ?? resourceFromContext;
  const { handleSubmit, reset } = useFormContext<EditableRecord>();
  const { isDirty, isSubmitting, isValidating } = useFormState();
  const hasChanges = Boolean(isDirty);
  const saveDisabled = !hasChanges || isSubmitting || isValidating;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTab = searchParams.get('returnTab');
  const keyboardInset = useKeyboardInset();

  const closeEditor = (savedRecord?: EditableRecord) => {
    if (!resource || redirectTo === false) return;
    const recordId = savedRecord?.id ?? record?.id;
    if (redirectTo === 'show' && recordId) {
      const tabQuery =
        returnTab && returnTab !== 'overview' ? `?tab=${encodeURIComponent(returnTab)}` : '';
      navigate(`/${resource}/${recordId}/show${tabQuery}`);
      return;
    }
    if (redirectTo === 'edit' && recordId) {
      redirect('edit', resource, recordId);
      return;
    }
    redirect('list', resource);
  };

  const persistRecord = (values: EditableRecord) =>
    new Promise<EditableRecord | null>((resolve, reject) => {
      if (!save) {
        resolve(null);
        return;
      }

      void save(values, {
        onSuccess: (savedRecord) => {
          const nextRecord = {
            ...(record ?? {}),
            ...values,
            ...((savedRecord as EditableRecord | undefined) ?? {})
          };
          reset(nextRecord);
          notify('Saved', { type: 'success' });
          resolve(nextRecord);
        },
        onError: (error) => {
          notify(`Error: ${error?.toString?.() || 'Unknown error'}`, { type: 'error' });
          reject(error instanceof Error ? error : new Error(String(error ?? 'Save failed')));
        }
      });
    });

  const submitRecord = async (): Promise<EditableRecord | null> => {
    let savedRecord: EditableRecord | null = null;
    try {
      await handleSubmit(async (values) => {
        savedRecord = await persistRecord(values);
      })();
    } catch {
      // persistRecord already notified the user. Catch only after react-hook-form
      // has observed the rejection and kept isSubmitSuccessful false.
      return null;
    }
    return savedRecord;
  };

  const handleSave = async () => {
    const savedRecord = await submitRecord();
    // A new record would stay on the create form, where the next Save adds it
    // again. Its edit page saves changes to the record that now exists.
    if (!record?.id && savedRecord?.id && resource) {
      redirect('edit', resource, savedRecord.id);
    }
  };

  const handleSaveAndClose = async () => {
    const savedRecord = await submitRecord();
    if (savedRecord) {
      closeEditor(savedRecord);
    }
  };

  return (
    <>
      <AppBarActionsPortal>
        {showDelete ? (
          <DeleteButton
            mutationMode="pessimistic"
            redirect="list"
            size="medium"
            sx={{ display: { xs: 'none', md: 'inline-flex' }, mr: 1 }}
          />
        ) : null}
        <Button variant="text" color="inherit" size="medium" onClick={() => closeEditor()}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={handleSaveAndClose}
          disabled={saveDisabled}
        >
          Save & Close
        </Button>
        <Button variant="outlined" size="medium" onClick={handleSave} disabled={saveDisabled}>
          Save
        </Button>
      </AppBarActionsPortal>

      <Box sx={{ display: { xs: 'block', md: 'none' }, height: 72 }} aria-hidden />
      <Portal>
        <Box
          role="toolbar"
          aria-label="Record actions"
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: keyboardInset,
            zIndex: saveBarZIndex,
            gap: { xs: 0.5, sm: 1 },
            alignItems: 'center',
            px: { xs: 1, sm: 1.5 },
            pt: 1,
            pb: 'calc(8px + env(safe-area-inset-bottom))',
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -6px 20px rgba(16, 24, 40, 0.08)'
          }}
        >
          {showDelete ? (
            <DeleteButton
              mutationMode="pessimistic"
              redirect="list"
              size="small"
              sx={{ mr: 'auto' }}
            />
          ) : (
            <Box sx={{ mr: 'auto' }} />
          )}
          <Button variant="text" color="inherit" size="small" onClick={() => closeEditor()}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveAndClose}
            disabled={saveDisabled}
          >
            Save & Close
          </Button>
          <Button variant="outlined" size="small" onClick={handleSave} disabled={saveDisabled}>
            Save
          </Button>
        </Box>
      </Portal>
    </>
  );
};

export default AppBarSaveActions;
